import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Clock, Video, MessageCircle, LogOut, Receipt, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import API, { formatApiError } from "@/lib/api";
import { toEmbedUrl } from "@/lib/video";

const BuyerLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post("/buyer/login", { email: email.trim(), mobile: mobile.trim() });
      onLogin();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-[#FAF6F0] rounded-3xl soft-shadow p-8" data-testid="buyer-login-card">
        <h1 className="font-display text-3xl font-bold text-center">
          <span className="text-[#2D5A47]">Binfinite</span> <span className="text-[#D98E32] italic">mind</span>
        </h1>
        <p className="text-sm text-[#5A6E63] text-center mt-2">
          Sign in with the email &amp; mobile number you used during purchase
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="buyer-login-form">
          <div>
            <Label htmlFor="buyer-email" className="text-xs text-[#5A6E63]">Email Id</Label>
            <Input id="buyer-email" data-testid="buyer-login-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
              className="mt-1 bg-[#FDFBF7] border-[#2D5A47]/20" />
          </div>
          <div>
            <Label htmlFor="buyer-mobile" className="text-xs text-[#5A6E63]">Mobile number</Label>
            <Input id="buyer-mobile" data-testid="buyer-login-mobile" type="tel" required value={mobile}
              onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile number"
              className="mt-1 bg-[#FDFBF7] border-[#2D5A47]/20" />
          </div>
          <Button type="submit" data-testid="buyer-login-submit" disabled={loading}
            className="w-full rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7] h-11">
            {loading ? "Signing in..." : "View My Classes"}
          </Button>
        </form>
        <Link to="/" className="block text-center text-xs text-[#5A6E63] mt-4 hover:text-[#2D5A47]" data-testid="buyer-back-home">
          &larr; Back to home
        </Link>
      </div>
    </div>
  );
};

const PurchaseCard = ({ item }) => {
  const s = item.session || {};
  return (
    <div className="bg-[#FAF6F0] rounded-3xl overflow-hidden soft-shadow border border-[rgba(45,90,71,0.1)]"
      data-testid={`enrolled-card-${item.registration_id}`}>
      {s.banner_url && <img src={s.banner_url} alt={s.title} className="w-full h-40 object-cover" />}
      <div className="p-5">
        <h3 className="font-display text-xl font-bold text-[#2D5A47]">{s.title || item.session_title}</h3>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5A6E63]">
          <span className="flex items-center gap-1"><CalendarDays size={13} /> {s.date}</span>
          <span className="flex items-center gap-1"><Clock size={13} /> {s.time} &middot; {s.duration}</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {s.zoom_link && (
            <a href={s.zoom_link} target="_blank" rel="noopener noreferrer" data-testid={`zoom-link-${item.registration_id}`}>
              <Button size="sm" className="rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7]">
                <Video size={14} className="mr-1.5" /> Join Zoom Class
              </Button>
            </a>
          )}
          {s.whatsapp_link && (
            <a href={s.whatsapp_link} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-link-${item.registration_id}`}>
              <Button size="sm" variant="outline" className="rounded-full border-[#D98E32]/50 text-[#D98E32] hover:bg-[#F9EBEA]">
                <MessageCircle size={14} className="mr-1.5" /> WhatsApp Group
              </Button>
            </a>
          )}
        </div>
        <div className="mt-4 pt-3 border-t border-[#2D5A47]/10 flex items-center gap-2 text-xs text-[#5A6E63]">
          <Receipt size={13} />
          <span data-testid={`receipt-info-${item.registration_id}`}>
            Paid &#8377;{item.amount} &middot; {item.paid_at ? new Date(item.paid_at).toLocaleString("en-IN") : ""}
            {item.razorpay_payment_id ? ` · ${item.razorpay_payment_id}` : ""}
          </span>
        </div>
      </div>
    </div>
  );
};

const CourseWatchCard = ({ item, onPlay }) => {
  const c = item.course || {};
  const lessons = c.lessons || [];
  return (
    <div className="bg-[#FAF6F0] rounded-3xl overflow-hidden soft-shadow border border-[rgba(45,90,71,0.1)]"
      data-testid={`course-enrolled-card-${item.enrollment_id}`}>
      {c.banner_url && <img src={c.banner_url} alt={c.title} className="w-full h-40 object-cover" />}
      <div className="p-5">
        <h3 className="font-display text-xl font-bold text-[#2D5A47]">{c.title}</h3>
        <p className="text-xs text-[#5A6E63] mt-1">{lessons.length} lesson(s) &middot; Lifetime access</p>
        <ul className="mt-4 space-y-2">
          {lessons.map((l, i) => (
            <li key={i}>
              <button data-testid={`lesson-play-${item.enrollment_id}-${i}`}
                onClick={() => onPlay({ url: l.video_url, title: l.title })}
                className="w-full flex items-center gap-2.5 text-left rounded-xl bg-[#FDFBF7] border border-[#2D5A47]/10 px-3 py-2.5 hover:border-[#D98E32] transition-colors">
                <PlayCircle size={18} className="text-[#D98E32] shrink-0" />
                <span className="text-sm text-[#2D5A47] font-medium truncate">{i + 1}. {l.title}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-4 pt-3 border-t border-[#2D5A47]/10 flex items-center gap-2 text-xs text-[#5A6E63]">
          <Receipt size={13} />
          <span data-testid={`course-receipt-${item.enrollment_id}`}>
            Paid &#8377;{item.amount} &middot; {item.paid_at ? new Date(item.paid_at).toLocaleString("en-IN") : ""}
          </span>
        </div>
      </div>
    </div>
  );
};

export default function BuyerDashboard() {
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState(null);

  const fetchDashboard = useCallback(() => {
    setLoading(true);
    API.get("/buyer/dashboard")
      .then((res) => { setData(res.data); setAuthed(true); })
      .catch(() => { setData(null); setAuthed(false); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (!authed && !loading) return <BuyerLogin onLogin={fetchDashboard} />;

  const logout = async () => {
    await API.post("/buyer/logout").catch(() => {});
    setAuthed(false);
    setData(null);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="buyer-dashboard">
      <header className="border-b border-[rgba(45,90,71,0.15)] bg-[#FDFBF7]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          <Link to="/" className="flex items-center" data-testid="dashboard-logo">
            <img src="/logo.jpeg" alt="Binfinite Mind" className="h-10 mix-blend-multiply" />
          </Link>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="buyer-logout-btn" className="text-[#5A6E63]">
            <LogOut size={14} className="mr-1.5" /> Logout
          </Button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {loading || !data ? (
          <p className="text-center text-[#5A6E63]" data-testid="dashboard-loading">Loading your classes...</p>
        ) : (
          <>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#2D5A47]" data-testid="dashboard-greeting">
              Welcome, {data.name}
            </h1>
            <p className="text-sm text-[#5A6E63] mt-1">Your purchased live sessions and video courses</p>
            {(data.purchases.length === 0 && (data.courses || []).length === 0) ? (
              <p className="mt-10 text-center text-[#5A6E63]" data-testid="dashboard-empty">
                No active purchases found. <Link to="/" className="text-[#D98E32] underline">Explore sessions &amp; courses</Link>
              </p>
            ) : (
              <>
                {data.purchases.length > 0 && (
                  <section className="mt-8" data-testid="dashboard-sessions-section">
                    <h2 className="font-display text-xl font-bold text-[#2D5A47] mb-4">Live Sessions</h2>
                    <div className="grid sm:grid-cols-2 gap-6">
                      {data.purchases.map((item) => (
                        <PurchaseCard key={item.registration_id} item={item} />
                      ))}
                    </div>
                  </section>
                )}
                {(data.courses || []).length > 0 && (
                  <section className="mt-10" data-testid="dashboard-courses-section">
                    <h2 className="font-display text-xl font-bold text-[#2D5A47] mb-4">Video Courses</h2>
                    <div className="grid sm:grid-cols-2 gap-6">
                      {data.courses.map((item) => (
                        <CourseWatchCard key={item.enrollment_id} item={item} onPlay={setPlayer} />
                      ))}
                    </div>
                  </section>
                )}
              </>
            )}
          </>
        )}
      </main>
      <Dialog open={!!player} onOpenChange={(o) => !o && setPlayer(null)}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-3xl bg-[#1E2A24] rounded-2xl p-0 overflow-hidden border-none [&>button]:text-[#FDFBF7] [&>button]:opacity-90 [&>button:hover]:opacity-100 [&>button]:z-10" data-testid="course-player-dialog">
          <DialogTitle className="sr-only">{player?.title || "Lesson"}</DialogTitle>
          <div className="aspect-video w-full bg-black">
            {player && (
              <iframe title={player.title} src={toEmbedUrl(player.url)} className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen />
            )}
          </div>
          <p className="text-[#FDFBF7] text-sm px-4 py-3" data-testid="player-lesson-title">{player?.title}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
